import { Recapt } from '../../engine';
import { StorageManager } from '../../managers/storageManager';
import { RECORD_ENGINE_STORAGE_KEY } from '../../managers/storageConstants';
import type { RecordOptions } from '../../types';
import { BaseModule } from '../base';
import { Namespace } from '../types';
import type { AdapterCallbacks, RecordAdapter } from './adapterTypes';
import {
  EventSourceType,
  InactiveTime,
  RecordEvent,
  UnProcessedEventStore,
} from './types';

const VISIBLE_LENGTH_OF_INACTIVE_TIME = 2000;
const INACTIVE_TIME_THRESHOLD = 10000;

/**
 * Module for recording and managing page interactions.
 * Delegates to a registered RecordAdapter for the actual recording mechanism.
 */
export class RecordModule extends BaseModule<RecordEvent> {
  public namespace: Namespace = 'record';

  private static _adapter: RecordAdapter | null = null;

  private inactiveTimesToSync: Record<number, InactiveTime> = {};

  private unProcessedEventStore: UnProcessedEventStore = {
    lastEventTimestamp: -1,
    lastInactiveTimeEventAfter: -1,
    currentCarry: 0,
  };

  private latestEventTypes: Array<{
    type: EventSourceType;
    timestamp: number;
  }> = [];

  public static registerAdapter(adapter: RecordAdapter) {
    RecordModule._adapter = adapter;
  }

  private get adapter(): RecordAdapter | null {
    return RecordModule._adapter;
  }

  public takeFullSnapshot() {
    this.adapter?.takeFullSnapshot();
  }

  private createCallbacks(): AdapterCallbacks {
    return {
      getRenderTimestamp: (ts) => this.getRenderTimestamp(ts),
      pushEvent: (event) => this.state.push(event),
      handleInactiveTime: (ts) => this.handleInactiveTime(ts),
      pushLatestEventType: (type, ts) => {
        if (this.latestEventTypes.length > 100) {
          this.latestEventTypes.shift();
        }
        this.latestEventTypes.push({ type, timestamp: ts });
      },
      purgeAllState: () => this.purgeState(),
    };
  }

  onNewSession(): void {
    super.onNewSession();
    this.adapter?.onNewSession();
  }

  public getRequestNamespace() {
    return this.adapter?.getRequestNamespace() ?? this.namespace;
  }

  onStart() {
    if (!this.adapter) {
      Recapt.warn('No record adapter registered. Recording will be disabled.');
      return;
    }

    const currentEngine = this.adapter.getEngineName();
    const previousEngine = StorageManager.getItem(RECORD_ENGINE_STORAGE_KEY);

    StorageManager.setItem(RECORD_ENGINE_STORAGE_KEY, currentEngine);

    if (previousEngine && previousEngine !== currentEngine) {
      Recapt.debug(
        `record engine changed from ${previousEngine} to ${currentEngine}, resetting`,
      );
      Recapt.reset();
      return;
    }

    const stored = this.loadStore<{
      state: RecordEvent[];
      unProcessedEventStore: UnProcessedEventStore;
      inactiveTimesToSync: Record<number, InactiveTime>;
      adapterState: any;
      keyMappings?: Record<number, string>;
      keyMappingsToSync?: number[];
      cssResolutionStore?: any;
    }>();

    if (stored) {
      this.state = stored.state ?? [];
      this.unProcessedEventStore = stored.unProcessedEventStore ?? {
        lastEventTimestamp: -1,
        lastInactiveTimeEventAfter: -1,
        currentCarry: 0,
      };
      this.inactiveTimesToSync = stored.inactiveTimesToSync ?? {};

      if (stored.adapterState) {
        this.adapter.restoreStoredState(stored.adapterState);
      } else {
        this.adapter.restoreStoredState({
          keyMappings: stored.keyMappings,
          keyMappingsToSync: stored.keyMappingsToSync,
          cssResolutionStore: stored.cssResolutionStore,
        });
      }
    }

    this.store(() => ({
      state: this.state,
      unProcessedEventStore: this.unProcessedEventStore,
      inactiveTimesToSync: this.inactiveTimesToSync,
      adapterState: this.adapter?.getStorableState() ?? {},
    }));

    this.adapter.init(this.createCallbacks());
    this.adapter.startRecording();
  }

  public setOptions(opts?: RecordOptions) {
    if (!opts) return;
    this.adapter?.setOptions(opts);
  }

  public updateRrRecordSettings(settings: { maskTextSelector?: string }) {
    this.adapter?.updateRecordSettings(settings);
  }

  onResume(): void {
    this.adapter?.startRecording();
  }

  onPause(): void {
    this.adapter?.stopRecording();
  }

  onStop() {
    this.adapter?.cleanup();
  }

  purgeState() {
    this.state = [];
    this.unProcessedEventStore = {
      lastEventTimestamp: -1,
      lastInactiveTimeEventAfter: -1,
      currentCarry: 0,
    };
    this.inactiveTimesToSync = {};
    this.latestEventTypes = [];
    this.adapter?.purgeState();
  }

  buildRequest(
    state: RecordEvent[],
  ): Record<string, unknown> & { state: RecordEvent[] } {
    const inactiveTimesToSync: Record<number, InactiveTime> = {};
    for (const keyToSync of Object.keys(this.inactiveTimesToSync)) {
      inactiveTimesToSync[keyToSync as unknown as number] =
        this.inactiveTimesToSync[keyToSync as unknown as number];
    }
    this.inactiveTimesToSync = {};

    return {
      state,
      inactiveTimes: inactiveTimesToSync,
      ...(this.adapter?.buildRequestExtras() ?? {}),
    };
  }

  public getLatestEventTypes() {
    return this.latestEventTypes.slice();
  }

  public getRenderTimestamp(timestamp: number) {
    return timestamp - this.unProcessedEventStore.currentCarry;
  }

  private handleInactiveTime(eventTimestamp: number) {
    const lastEventTimestamp = this.unProcessedEventStore.lastEventTimestamp;

    if (lastEventTimestamp !== -1) {
      if (eventTimestamp - lastEventTimestamp > INACTIVE_TIME_THRESHOLD) {
        Recapt.debug('detected inactive time');

        const duration = eventTimestamp - lastEventTimestamp;

        this.unProcessedEventStore.currentCarry +=
          duration - VISIBLE_LENGTH_OF_INACTIVE_TIME;

        this.inactiveTimesToSync[lastEventTimestamp] = {
          eventBefore: lastEventTimestamp,
          eventAfter: eventTimestamp,
          duration,
          timeBetweenPreviousInactive: -1,
        };

        if (this.unProcessedEventStore.lastInactiveTimeEventAfter !== -1) {
          this.inactiveTimesToSync[
            lastEventTimestamp
          ]!.timeBetweenPreviousInactive =
            lastEventTimestamp -
            this.unProcessedEventStore.lastInactiveTimeEventAfter;
        }

        this.unProcessedEventStore.lastInactiveTimeEventAfter =
          this.inactiveTimesToSync[lastEventTimestamp].eventAfter;
      }
    }

    this.unProcessedEventStore.lastEventTimestamp = eventTimestamp;
  }
}
